// Panel de las tandas + creaciones — ADR-2026-08-20-tanda-en-el-panel y
// ADR-2026-08-20-varias-tandas-en-el-panel.
//
// Reemplaza a la columna de configuración (Modelo + Pose) del `configure` a dos
// columnas de "Imagen con modelo" en cuanto se aprieta Generar: arriba las tandas
// —un recuadro por imagen, con el mismo lenguaje visual que la pantalla de carga
// full-screen que había antes— y debajo las últimas creaciones del usuario, que
// también van por LOTE y no en una bolsa común
// (ADR-2026-08-25-lotes-con-fecha-relativa): todo lo que salió de un mismo toque
// de Generar va junto, bajo un título que lo cuenta y lo fecha
// ("2 imágenes • Hace 15 minutos").
//
// Son VARIAS tandas, no una: se puede seguir generando con una generación en curso,
// y cada tanda nueva entra ARRIBA de las anteriores (la lista viene ordenada de la
// más nueva a la más vieja).
//
// Por qué reemplaza al overlay: el overlay tapaba la pantalla entera para decir
// "esperá", y lo que se quiere mostrar es JUSTO lo que tapaba. Acá el usuario ve
// cuántas imágenes vienen, con qué forma, y sigue teniendo el panel de la
// izquierda a mano para preparar la próxima tanda.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Clapperboard, Download, ImageOff, Images, Loader2, Play, Plus, RotateCcw, AudioLines } from 'lucide-react';
import { FondoCarga, NucleoCarga } from '../../../components/loading/NucleoCarga';
import {
  DURACION_BARRA_IMAGEN_MS,
  progresoPaceado,
  useProgresoDesde,
} from '../../../components/loading/useGenerationProgress';
import { disposicionDeCarga } from '../../../components/loading/disposicionDeCarga';
import { useCreacionesRecientes } from '../../../hooks/useCreacionesRecientes';
import { DURACION_BARRA_VIDEO_MS } from '../recuadrosEnCard';
import { filtrarPorContenido, type FiltroContenido } from '../../../utils/filtroContenido';
import { useVerificacion } from '../verificacion/useVerificacion';
import { AccionRapidaCreacion } from './AccionRapidaCreacion';
import { useDatosDeCeldas, type DatosCelda } from './useDatosDeCeldas';
import { useTiendaConectada } from '../../../hooks/useTiendaConectada';
import { useOpcionesDeDescarga } from '../../../components/descarga/useOpcionesDeDescarga';
import { descargarCreacion as bajarCreacion } from './descargarCreacion';
import { CapaVerificacion } from '../verificacion/CapaVerificacion';
import {
  GeneratedImagesHistory,
  type FilaInicialVisor,
} from '../../GeneratedImagesHistory';
import { firmarEnLote } from '../../../utils/firmarEnLote';
import { filaInicialDeCelda, precargarOriginal } from './filaInicialDelVisor';
import { fechaSmart, grupoDeFecha, horaDe } from '../../../utils/fechaSmart';
import { useNombresDelEquipo } from '../../../hooks/useNombresDelEquipo';
import { MODE_KIND_TO_PATH, nombreCortoModo } from '../modesCatalog';
import { useRegistrarPantallaDeCarga } from '../useGeneracionesEnCurso';
import type { GeneracionDelegada } from './generacionDelegada';
import type { ChatAttachment } from '../agentAttachments';
import { adjuntoDeCelda } from '../../../chat-atlas/lienzo/accionesDeCelda';
import { PanelDeHerramienta } from '../../../chat-atlas/lienzo/herramientas/PanelDeHerramienta';
import type { PedidoDeHerramienta } from '../../../chat-atlas/lienzo/herramientas/herramienta';
import { ratioDeFormato } from '../../../utils/ratioDeFormato';
import type { AccionVisor } from '../../GeneratedImagesHistory';
import { useCreacionesEliminadas } from '../../../lib/creacionesEliminadas';

/**
 * La forma de TODAS las cards de la columna, terminadas y generando —
 * ADR-2026-09-06-la-columna-de-resultados-va-toda-en-4-5.
 *
 * 4:5 (0.8), el formato más común de la app y el que ya usaba el esqueleto de carga.
 * Antes cada card tomaba el ratio de SU tanda, así que una columna con un banner 16:9,
 * un cuadrado y un vertical era una grilla de tres alturas distintas por fila: se leía
 * como un collage roto y no como un conjunto. Las que no son 4:5 se recortan al centro
 * (`object-cover`) y se abren enteras en el visor.
 */
const RATIO_CARD = 0.8;

/** Imagen ya terminada de la tanda. */
export interface ImagenDeTanda {
  id: string;
  src: string;
  /**
   * Es un video (`src` es el thumbnail): la card lleva el play. Lo trae "Animar" desde
   * el visor (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna). Sin esto
   * hereda el `esVideo` de la tanda.
   */
  esVideo?: boolean;
}

/** Una tanda: lo que salió de UN toque de Generar. */
export interface TandaPanel {
  /** Identidad propia — la generación que la disparó actualiza ESTA y no la de al
   *  lado, que puede estar corriendo al mismo tiempo. */
  id: string;
  /** Cuántas imágenes se pidieron: es la cantidad de recuadros. */
  total: number;
  /** Las que ya volvieron. Mientras `imagenes.length < total`, el resto sigue en
   *  estado "generando". */
  imagenes: ImagenDeTanda[];
  /** Proporción ancho/alto de la tanda (del formato elegido) — los recuadros
   *  vacíos tienen que tener la forma de lo que va a salir. Es POR TANDA: entre una
   *  y otra el usuario puede haber cambiado el formato. */
  ratio: number;
  /** La tanda terminó (todas las imágenes llegaron). */
  terminada: boolean;
  /**
   * Cuándo se apretó Generar (epoch en ms). Encabeza la tanda como fecha
   * relativa, igual que los lotes de antes
   * (ADR-2026-08-25-lotes-con-fecha-relativa).
   *
   * Lo pone quien crea la tanda y no este panel: el panel se puede desmontar y
   * volver a montar (se cambia de modo, se abre y cierra el sheet) y ahí un
   * "cuándo lo vi por primera vez" reiniciaría todos los relojes a cero.
   */
  creadaEn: number;
  /**
   * De qué MODO salió (`preset.kind`).
   *
   * La columna sólo dibuja las tandas del modo que está abierto
   * (ADR-2026-09-04-la-tanda-no-se-muda-de-modo): las de otro modo siguen corriendo y
   * vuelven a verse al volver a él, pero no se cuelan en una pantalla que no las pidió.
   */
  modo?: string;
  /**
   * De qué producto es la tanda — lo rotula cada imagen, junto con la hora
   * (ADR-2026-09-04-la-columna-de-resultados-va-en-una-grilla-plana).
   *
   * Lo pasa quien dispara la generación: el panel no conoce la selección, y el nombre
   * tiene que ser el que estaba puesto al apretar Generar y no el que haya después.
   * Vacío = la card muestra sólo la hora.
   */
  producto?: string;
  /**
   * La tanda produce VIDEO: es "Animar" pedido desde el visor prestado, que en desktop
   * aterriza en la columna como una tanda más
   * (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna). El recuadro dice
   * "Generando video…" y las cards terminadas llevan el play.
   */
  esVideo?: boolean;
  /**
   * Cuánto se estima que tarda (ms): pacea la barra de sus recuadros. Sin dato, el
   * recuadro usa el default de imagen o de video según `esVideo`
   * (ADR-2026-09-18-la-barra-del-video-se-pacea-con-lo-que-tarda-de-verdad).
   */
  duracionEstimadaMs?: number;
}

/** Cuántas creaciones anteriores se traen para llenar la grilla. */
const LIMITE_CREACIONES = 18;

/**
 * Cuántas cards se dibujan de entrada, y cuántas se suman cada vez que el usuario llega
 * al final — ADR-2026-09-04-las-creaciones-del-panel-se-cargan-por-tanda.
 *
 * La columna traía 18 creaciones y las pintaba TODAS de una: decenas de `<img>` pedidas
 * juntas para una lista que casi nunca se recorre entera. De a 12, lo que se pide al
 * abrir es lo de arriba —que es lo que se está mirando— y el resto entra a medida que se
 * baja.
 *
 * 12 y no 4: la grilla tiene hasta tres columnas, así que una página tiene que llenar
 * varias filas. Con menos, cualquier scroll corto pide otra y el centinela se dispara
 * todo el tiempo.
 */
const CELDAS_POR_PAGINA = 12;

export interface PanelTandaCreacionesProps {
  /** Las tandas, de la MÁS NUEVA a la más vieja: así entra arriba la que se acaba
   *  de disparar (ADR-2026-08-20-varias-tandas-en-el-panel). */
  tandas: TandaPanel[];
  /** Empresa, para el listado de creaciones. */
  companyId: string | null;
  /** Marca activa: el listado respeta el mismo filtro que el historial. */
  brandId?: string | null;
  /**
   * Avisa que se abrió/cerró el visor de una creación. El sheet que contiene a
   * este panel es `fixed z-[100]` y el visor del historial monta su lightbox
   * como `fixed z-50` PEGADO AL BODY (portal propio), así que sin bajarle el z
   * al sheet el visor queda debajo y sólo se ve su velo negro. Subir el z del
   * lightbox no sirve: los menús del propio visor (upscale, video, filtros) son
   * `z-[200]` y quedarían tapados por él.
   */
  onVisorChange?: (abierto: boolean) => void;
  /**
   * Cambia cuando el que genera necesita que la lista se vuelva a pedir.
   *
   * Pasa cuando una tanda cierra sin imágenes que dibujar —el preload no llegó a
   * resolverlas—: antes eso mandaba al usuario a la pantalla vieja de resultados; ahora
   * se re-consulta y las imágenes entran por acá, que es donde ya está mirando
   * (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
   */
  recargarToken?: number;
  /**
   * Carrusel / Animar pedidos DESDE el visor prestado, en desktop: el visor arma la
   * promesa, se cierra y se la entrega a quien tiene la columna, que la muestra como
   * una tanda más (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna). Sin esto
   * el visor se comporta como en la home: loader full-screen y navegación.
   */
  onGeneracionDelegada?: (g: GeneracionDelegada) => void;
  /**
   * El `kind` del modo abierto. Las celdas de OTRO modo llevan un chip con el nombre del
   * suyo: la columna dibuja las tandas de toda la empresa
   * (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos) y sin el rótulo una card de
   * Fotos de producto en la columna de Modelo no dice de dónde salió. Las del modo
   * abierto no lo llevan: sería repetir el título de la pantalla en cada card.
   */
  modoActual?: string;
  /**
   * La empresa tiene prendida "Verificación de calidad" (Funciones avanzadas): cada card
   * de imagen con producto lleva el botón de analizar y, después, la barra de resultado
   * (ADR-2026-09-07-verificacion-de-calidad-en-la-columna). Sin esto la columna es la
   * de siempre.
   */
  verificacionActiva?: boolean;
  /**
   * Además de la función, la empresa prendió el análisis AUTOMÁTICO: cada imagen recién
   * generada se analiza sola (ADR-2026-09-07-analisis-automatico-al-generar). Solo las de
   * las tandas de esta sesión; el historial se sigue analizando a mano.
   */
  analisisAutomatico?: boolean;
  /**
   * Cuántas columnas tiene la grilla. Default 3 fijas — la decisión para la columna de
   * Modelo (ADR-2026-09-06-la-columna-de-resultados-va-de-a-tres). `'auto'` reparte por
   * ancho (`auto-fill`, piso 240px): para cuando la grilla ocupa la pantalla entera, como
   * en Creador de video (ADR-2026-09-07-modo-creador-video), donde tres columnas eran
   * cards de 500px.
   */
  columnas?: number | 'auto';
  /**
   * Qué tipo de contenido se muestra — ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido.
   *
   * Default `'todo'`: la columna es la de siempre en todos los modos menos Creador de
   * video, que abre en `'videos'` porque es un modo que sólo hace videos y sin el filtro
   * su grilla es mayormente fotos de otros modos.
   *
   * Recorta las DOS fuentes: la consulta del historial (en la base) y las celdas ya
   * armadas (que es lo que esconde las tandas de imagen de otro modo, incluso mientras
   * están generando).
   */
  filtro?: FiltroContenido;
  /**
   * Sólo las creaciones de un modo (`modo_utilizado`): la pestaña Historial de Recrear video
   * (ADR-2026-10-09-recrear-video-adentro-de-recrear). Recorta la consulta del historial;
   * las tandas en curso se siguen viendo todas, como en cualquier columna.
   */
  modoUtilizado?: string;
  /**
   * La card en modo "limpia" del modo Redes (ADR-2026-10-02-redes-acciones-en-la-card): el
   * rótulo (producto y hora) y las acciones aparecen recién al pasar el mouse, y en la
   * esquina superior derecha hay una columna de acciones de sólo ícono —Descargar, Editar,
   * Carrusel y Animar— con su nombre a la izquierda al pasar por cada una. "Editar" no
   * abre nada: entrega la creación como adjunto (`onEditar`) para que la pantalla la ponga
   * de referencia. Carrusel y Animar abren el visor con esa acción ya disparada. Sin esta
   * prop, la card es la de siempre.
   */
  accionesEnHover?: {
    /**
     * `origen` es el rectángulo de la card tocada y `src` su miniatura: la pantalla los usa
     * para animar la foto volando hacia donde va a quedar como referencia.
     */
    onEditar: (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }) => void;
    /**
     * "Rehacer": la pantalla vuelve a poner en su caja lo que generó esta creación. El
     * botón sólo se dibuja en las cards para las que `puedeRehacer` dice que sí (las que
     * guardaron con qué se hicieron); las dos reciben el `datos_generacion` de la fila
     * (ADR-2026-10-04-rehacer-desde-la-cuadricula).
     */
    puedeRehacer?: (datosGeneracion: unknown) => boolean;
    onRehacer?: (datosGeneracion: unknown) => void;
  };
}

/** El `grid-template-columns` de las grillas del panel — ver `columnas`. */
function columnasDeGrilla(columnas: number | 'auto'): string {
  return columnas === 'auto'
    ? 'repeat(auto-fill, minmax(240px, 1fr))'
    : `repeat(${columnas}, minmax(0, 1fr))`;
}

/**
 * Progreso de la barra a partir de un instante fijo (`creadaEn`), con el pacing de
 * la pantalla de carga (`progresoPaceado`, tope 97%).
 *
 * Tickea con un `setInterval` de 250 ms y no con un RAF: hasta 18 recuadros a 60 fps
 * con un `setState` por frame para un riel de 4 px es trabajo tirado, y la transición
 * de 300 ms del relleno alisa los saltos entre ticks
 * (ADR-2026-09-06-nucleo-de-carga-compartido).
 */
// (vive en `components/loading/useGenerationProgress`: lo comparte con la card del
//  chat — ADR-2026-09-08-la-espera-del-chat-usa-la-pantalla-de-carga)

/**
 * El rótulo de "de qué modo es" que llevan las celdas ajenas: pastilla chica, arriba a la
 * izquierda, translúcida sobre la foto o sobre el azul del recuadro. Un solo componente
 * para las dos porque tienen que leerse igual — es la misma pregunta con la misma
 * respuesta, esté la imagen o todavía no.
 */
const ChipDeModo: React.FC<{ texto: string }> = ({ texto }) => (
  <span className="pointer-events-none absolute left-2 top-2 z-20 inline-flex max-w-[calc(100%-1rem)] items-center truncate rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-semibold leading-tight text-white backdrop-blur-sm">
    {texto}
  </span>
);

/**
 * El bloque gris que ocupa el lugar de una card mientras la lista viaja.
 *
 * Uno solo para las DOS veces que aparece —la columna vacía del primer render y el hueco
 * debajo de la tanda mientras llega el historial
 * (ADR-2026-09-08-la-columna-de-resultados-muestra-el-hueco-del-historial)— porque tienen
 * que ser el mismo gris y la misma forma: si difieren, pasar de uno al otro se lee como un
 * cambio de cosa y no como la misma espera.
 */
const CeldaEsqueleto: React.FC = () => (
  <div className="aspect-[4/5] animate-pulse rounded-2xl bg-black/[0.05]" />
);

/**
 * Cuántos esqueletos van debajo de lo que ya hay para que la grilla quede en filas
 * enteras: primero se tapan los huecos que dejó la última fila y después se agrega UNA
 * fila más, que es lo que anuncia que abajo viene más.
 *
 * Con `'auto'` no se sabe cuántas columnas entran (las reparte el ancho), así que van 5.
 */
function esqueletosDeRelleno(yaHay: number, columnas: number | 'auto'): number {
  if (columnas === 'auto') return 5;
  return ((columnas - (yaHay % columnas)) % columnas) + columnas;
}

/**
 * Cuánto se desfasa la entrada de una card respecto de la anterior, en ms —
 * ADR-2026-09-08-la-columna-de-resultados-entra-escalonada.
 *
 * El módulo es `CELDAS_POR_PAGINA`: el desfase se cuenta DENTRO de la página, así la
 * segunda tanda de cards vuelve a arrancar en cero en vez de esperar medio segundo por
 * una ola que empezó arriba de todo. Con 12 celdas y 30 ms, la ola completa dura ~330 ms
 * —se lee como un barrido y no como una cola.
 */
const DESFASE_ENTRADA_MS = 30;

/**
 * La miniatura de una card terminada, con su propio fundido al cargar —
 * ADR-2026-09-08-la-columna-de-resultados-entra-escalonada.
 *
 * Sin esto el `<img>` salta del gris del contenedor a la foto en un frame: la card ya
 * había entrado con su animación y la foto aparecía después, de golpe, adentro. Acá la
 * card entra vacía y la foto se funde encima cuando está.
 *
 * El `complete` del montaje no es paranoia: si la imagen ya está en la caché del
 * navegador puede terminar ANTES de que React enganche el `onLoad`, el evento no llega
 * nunca y la card se quedaría transparente para siempre.
 *
 * Un error de carga NO la muestra: se queda en 0 y se ve el gris del contenedor. Es a
 * propósito —Chrome dibuja su ícono de imagen rota y el `alt` como texto, que es
 * justo el placeholder feo que sacó ADR-2026-08-27-la-imagen-que-todavia-no-cargo-no-muestra-el-alt.
 */
const MiniaturaCreacion: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [cargada, setCargada] = useState(false);
  const ref = React.useRef<HTMLImageElement | null>(null);
  React.useEffect(() => {
    if (ref.current?.complete) setCargada(true);
  }, [src]);
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={() => setCargada(true)}
      // `object-cover`: la card se llena entera y un 16:9 muestra su franja del medio.
      // El visor la abre completa.
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
        cargada ? 'opacity-100' : 'opacity-0'
      }`}
    />
  );
};

/**
 * Recuadro "generando": el MISMO núcleo que la pantalla de carga full-screen
 * (`NucleoCarga` en su variante compacta, sobre el azul `#3f5bd6` con la foto de
 * fondo) — ADR-2026-09-06-nucleo-de-carga-compartido, que supersede el punto 4 de
 * ADR-2026-08-20-tanda-en-el-panel.
 *
 * Antes usaba `AnimatedBlurBg` (el fondo de la card "Automático" de los pickers) con
 * un ícono de lucide y una barra que barría en loop, desfasada por índice: otro
 * lenguaje que el de la espera full-screen que el usuario ya conocía.
 *
 * La barra es DETERMINADA y se siembra desde `creadaEn` con el mismo pacing que la
 * pantalla grande (`DURACION_BARRA_IMAGEN_MS`, tope 97%), SIN desfase: todos los
 * recuadros de una tanda avanzan en sincronía y se leen como UNA generación —que era
 * la objeción del ADR anterior al progreso individual—. Y como el reloj es el de la
 * tanda y no el del componente, remontar el panel (cambiar de modo, cerrar y abrir el
 * sheet) no reinicia la barra.
 *
 * Exportado: Creaciones lo pinta arriba de su grilla para las generaciones en curso
 * (ADR-2026-09-06-registro-de-generaciones-en-curso).
 */
export const RecuadroGenerando: React.FC<{
  ratio: number;
  creadaEn: number;
  esVideo?: boolean;
  /**
   * Deja lugar abajo para un rótulo encima del recuadro: lo usa Creaciones, que le
   * dibuja el producto y la hora como a las cards de al lado. Sin esto, en formatos
   * anchos (16:9) el degradé del rótulo tapaba el título y la barra.
   */
  espacioAbajo?: boolean;
  /**
   * Chip arriba a la izquierda con el nombre del modo del que viene — lo pone la columna
   * de resultados en las tandas de OTRO modo
   * (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos). Creaciones no lo pasa.
   */
  etiqueta?: string | null;
  /**
   * Pacing de la barra (ms). Quien conoce la configuración del pedido pasa la
   * estimación (`utils/estimacionVideo`); sin dato, el default es el de imagen (80 s) o
   * el de video (8 min) según `esVideo`. Antes el default era SIEMPRE el de imagen y la
   * columna del modo no pasaba nada: un video de 4 minutos clavaba 97 % al minuto
   * (ADR-2026-09-18-la-barra-del-video-se-pacea-con-lo-que-tarda-de-verdad).
   */
  duracionMs?: number;
}> = ({
  ratio,
  creadaEn,
  esVideo = false,
  espacioAbajo = false,
  etiqueta = null,
  duracionMs,
}) => {
  const pacing =
    duracionMs ?? (esVideo ? DURACION_BARRA_VIDEO_MS : DURACION_BARRA_IMAGEN_MS);
  const progreso = useProgresoDesde(creadaEn, pacing);
  // El recuadro se MIDE y el núcleo se acomoda a lo que hay: apilado si hay alto, en
  // fila si es bajo y ancho (16:9 en una grilla), mínimo si no entra texto
  // (ADR-2026-10-05-el-recuadro-de-carga-se-acomoda-a-su-tamano). La proporción sola no
  // alcanza: el mismo 16:9 mide 328×184 en el lienzo y 225×127 en una card del chat.
  const recuadroRef = useRef<HTMLDivElement | null>(null);
  const [medida, setMedida] = useState({ ancho: 0, alto: 0 });
  useLayoutEffect(() => {
    const el = recuadroRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => {
      const { width, height } = el.getBoundingClientRect();
      setMedida((m) =>
        Math.abs(m.ancho - width) < 1 && Math.abs(m.alto - height) < 1
          ? m
          : { ancho: width, alto: height },
      );
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);
  const disposicion = disposicionDeCarga(medida.ancho, medida.alto);
  return (
    <div
      ref={recuadroRef}
      className="relative overflow-hidden rounded-2xl bg-brand-primary-apagado text-white"
      style={{ aspectRatio: String(ratio) }}
    >
      <FondoCarga />
      {etiqueta && <ChipDeModo texto={etiqueta} />}
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center ${
          disposicion === 'fila' ? 'px-4' : 'px-3'
        } ${espacioAbajo ? 'pb-9' : ''}`}
      >
        <NucleoCarga
          variante="compacto"
          disposicion={disposicion}
          titulo={esVideo ? 'Generando video…' : 'Generando…'}
          progreso={progreso}
          // Un video tarda minutos y la barra sola no lo dice: la nota va debajo de
          // la barra, en TODAS las cards de video (columna del modo, Creaciones, chat,
          // lienzo) porque todas pasan por acá
          // (ADR-2026-09-19-las-cards-de-video-avisan-que-tardan).
          debajoDeLaBarra={esVideo ? 'Puede tardar varios minutos' : undefined}
        />
      </div>
    </div>
  );
};

export const PanelTandaCreaciones: React.FC<PanelTandaCreacionesProps> = ({
  tandas,
  companyId,
  brandId = null,
  onVisorChange,
  accionesEnHover,
  recargarToken = 0,
  onGeneracionDelegada,
  modoActual,
  verificacionActiva = false,
  analisisAutomatico = false,
  columnas = 3,
  filtro = 'todo',
  modoUtilizado,
}) => {
  // Esta columna YA muestra la carga: mientras esté montada, el aviso flotante de
  // "generando de fondo" no hace falta (ADR-2026-09-06-el-aviso-de-generando-de-fondo).
  useRegistrarPantallaDeCarga();
  /**
   * Nombre del modo de una celda, o `null` si es del modo abierto (o si no se sabe):
   * sólo las ajenas se rotulan.
   */
  const etiquetaDeModo = (modo: string | undefined): string | null => {
    if (!modo || modo === modoActual) return null;
    return nombreCortoModo(MODE_KIND_TO_PATH[modo] ?? '', '') || null;
  };
  // La imagen abierta en el visor. Es el visor ENTERO del historial, prestado
  // como hace la home (ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home): no
  // hay una segunda implementación de recortar/animar/descargar.
  const [creacionAbierta, setCreacionAbiertaState] = useState<string | null>(
    null,
  );
  /**
   * La card que se acaba de tocar, mientras el visor todavía no abrió: lleva un anillo
   * y un velo para que el toque tenga respuesta al instante. Con la fila ya armada
   * (ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia) el visor abre en el
   * frame siguiente y esto casi no se ve; sigue haciendo falta para el camino largo —
   * cuando los datos de la card no llegaron y el visor tiene que ir a buscar la fila y la
   * firma (ADR-2026-09-06-el-visor-prestado-abre-por-id). Se apaga con `onAbierto`, y
   * también al cerrar o fallar — la card no puede quedar marcada si el visor nunca llegó.
   */
  const [abriendo, setAbriendo] = useState<string | null>(null);
  /**
   * La fila con la que el visor va a abrir, armada en el toque —
   * ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia.
   *
   * `undefined` cuando el panel todavía no tiene todo (los datos de la card viajan por
   * lote): ahí el visor abre por su camino de siempre.
   */
  const [filaInicialVisor, setFilaInicialVisor] = useState<FilaInicialVisor | undefined>(
    undefined,
  );
  /** Con qué acción abre el visor (Carrusel / Animar desde la card limpia); sin ella, abre y ya. */
  const [accionInicialVisor, setAccionInicialVisor] = useState<AccionVisor | undefined>(undefined);
  /**
   * Carrusel / Animar de la card limpia abren su panel AHÍ MISMO, sin la vista ampliada:
   * los mismos paneles que el lienzo del Agente (`PanelDeHerramienta`), que no dependen
   * del lienzo. Lo que generan entra como una tanda más por `onGeneracionDelegada`.
   */
  const [pedidoHerramienta, setPedidoHerramienta] = useState<PedidoDeHerramienta | null>(null);
  /**
   * El editor de producto ("Cargar más fotos" de la verificación) está abierto. Necesita
   * lo MISMO que el visor: que el sheet le ceda el z-index, porque `VariantForm` portalea
   * al body por debajo de este `z-[100]`
   * (ADR-2026-09-09-el-editor-de-producto-le-pide-el-z-index-a-la-columna).
   */
  const [editorProductoAbierto, setEditorProductoAbierto] = useState(false);
  const setCreacionAbierta = (id: string | null, fila?: FilaInicialVisor, accion?: AccionVisor) => {
    setCreacionAbiertaState(id);
    setFilaInicialVisor(fila);
    setAccionInicialVisor(accion);
    setAbriendo(id);
  };
  // Un solo reporte para las DOS cosas que pueden abrirse encima: si cada una llamara a
  // `onVisorChange` por su cuenta, cerrar una devolvería el z-index con la otra abierta.
  useEffect(() => {
    if (editorProductoAbierto) onVisorChange?.(true);
    else onVisorChange?.(creacionAbierta !== null);
  }, [creacionAbierta, editorProductoAbierto, onVisorChange]);

  // Las creaciones anteriores se piden cuando este panel se monta —o sea, al
  // generar— y no antes: en el paso de configuración nadie las mira.
  //
  // `imagenes.length` en las deps del hook no existe, así que el listado NO se
  // refresca solo cuando termina la tanda; las recién generadas ya están
  // arriba, en la tanda, y duplicarlas abajo sería ruido.
  /**
   * Cuántas creaciones se le piden a la base. Crece cuando el usuario llegó al final de
   * lo que ya está traído y todavía hay más — ADR-2026-09-04-las-creaciones-del-panel-se-cargan-por-tanda.
   */
  const [limiteCreaciones, setLimiteCreaciones] = useState(LIMITE_CREACIONES);
  /** Cuántas cards se están DIBUJANDO de las que ya se trajeron. */
  const [celdasVisibles, setCeldasVisibles] = useState(CELDAS_POR_PAGINA);
  // Cambiar de empresa, de marca o de filtro es otra lista: vuelve a arrancar de la
  // primera página. Sin el `filtro` acá, pasar de "videos" a "todo" arrastraba el límite
  // inflado por la paginación anterior y pedía de golpe decenas de filas.
  React.useEffect(() => {
    setLimiteCreaciones(LIMITE_CREACIONES);
    setCeldasVisibles(CELDAS_POR_PAGINA);
  }, [companyId, brandId, filtro, modoUtilizado]);

  const {
    items: creaciones,
    cargando,
    hayMas,
  } = useCreacionesRecientes(
    companyId,
    limiteCreaciones,
    brandId,
    recargarToken,
    filtro === 'videos',
    modoUtilizado ?? null,
  );

  // Reloj compartido por todos los encabezados. Se refresca solo para que
  // "Hace unos segundos" pase a "Hace 2 minutos" sin que el usuario tenga que
  // tocar nada: el panel se queda abierto mientras la tanda corre, y un texto
  // congelado en "unos segundos" mientras se espera un minuto largo miente.
  const [ahora, setAhora] = useState(() => Date.now());
  React.useEffect(() => {
    const t = window.setInterval(() => setAhora(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  // Las tandas de ESTA sesión ya se muestran arriba con su propio encabezado. Si
  // el listado las trajera también (el hook vuelve a pedir al cambiar de marca,
  // y para entonces la tanda puede haber terminado), aparecerían dos veces.
  const idsEnTandas = React.useMemo(
    () => new Set(tandas.flatMap((t) => t.imagenes.map((i) => i.id))),
    [tandas],
  );

  /**
   * TODO lo que va en la grilla, en un solo array y de lo más nuevo a lo más viejo —
   * ADR-2026-09-04-la-columna-de-resultados-va-en-una-grilla-plana.
   *
   * Antes la columna se leía como una pila de bloques: cada toque de Generar abría su
   * propia sección con un encabezado ("2 Imágenes • Hace 15 minutos") y su grilla
   * adentro, así que dos tandas de una imagen ocupaban dos filas enteras para mostrar
   * dos fotos. Acá las imágenes van una al lado de la otra —vengan de la tanda en curso,
   * de una anterior de esta sesión o de una generación de ayer— y lo que decía el
   * encabezado (de qué es y cuándo salió) pasa a estar DENTRO de cada card, como en
   * Creaciones.
   *
   * Los recuadros "generando" abren la lista: son lo que el usuario está esperando.
   */
  const eliminadas = useCreacionesEliminadas();
  // "· Juli" al lado de la hora, sólo con más de un miembro (misma regla que Creaciones).
  const equipo = useNombresDelEquipo(companyId);
  const celdas = React.useMemo(() => {
    type Celda =
      | {
          tipo: 'generando';
          key: string;
          ratio: number;
          creadaEn: number;
          esVideo: boolean;
          modo?: string;
          duracionEstimadaMs?: number;
        }
      | {
          tipo: 'imagen';
          key: string;
          id: string;
          src: string;
          alt: string;
          ratio: number;
          esVideo: boolean;
          titulo: string;
          /**
           * Cuándo salió, en epoch — el grupo y el rótulo se derivan de acá.
           *
           * Antes viajaba ya formateada (`fechaSmart`), pero ahora el rótulo DEPENDE del
           * grupo en el que caiga la card (adentro de "Hoy" alcanza con la hora), y eso
           * se sabe recién al armar los grupos.
           */
          creadaEn: number;
          modo?: string;
          /** Quién la pidió. Las tandas en vivo no lo traen: son de quien está mirando. */
          userId?: string | null;
        };
    const out: Celda[] = [];
    for (const tanda of tandas) {
      const pendientes = Math.max(0, tanda.total - tanda.imagenes.length);
      for (let i = 0; i < pendientes; i++) {
        out.push({
          tipo: 'generando',
          key: `${tanda.id}-pendiente-${i}`,
          ratio: tanda.ratio,
          // El reloj de la barra es el de la tanda: así todos sus recuadros avanzan
          // juntos y remontar el panel no los reinicia.
          creadaEn: tanda.creadaEn,
          esVideo: tanda.esVideo ?? false,
          modo: tanda.modo,
          duracionEstimadaMs: tanda.duracionEstimadaMs,
        });
      }
      for (const img of tanda.imagenes) {
        // Eliminada en esta sesión desde el visor: no se dibuja más
        // (ADR-2026-10-04-eliminar-saca-la-creacion-de-la-vista).
        if (eliminadas.has(img.id)) continue;
        out.push({
          tipo: 'imagen',
          key: img.id,
          id: img.id,
          src: img.src,
          alt: tanda.producto || 'Imagen generada',
          ratio: tanda.ratio,
          esVideo: img.esVideo ?? tanda.esVideo ?? false,
          titulo: tanda.producto || '',
          creadaEn: tanda.creadaEn,
          modo: tanda.modo,
        });
      }
    }
    // Las de esta sesión ya están arriba: si el listado las trae también (vuelve a pedir
    // al cambiar de marca, y para entonces la tanda puede haber terminado), aparecerían
    // dos veces.
    for (const c of creaciones) {
      if (idsEnTandas.has(c.id) || eliminadas.has(c.id)) continue;
      out.push({
        tipo: 'imagen',
        key: c.id,
        id: c.id,
        src: c.src,
        alt: c.alt,
        // Sin `ratio` conocido cae a 4:5, el formato más común de la app; el recorte es
        // `object-cover`, así que la forma real de la foto no rompe nada.
        ratio: c.ratio ?? 0.8,
        esVideo: c.esVideo,
        titulo: c.alt,
        creadaEn: new Date(c.creadaEn).getTime(),
        userId: c.userId,
      });
    }
    // El filtro va al FINAL, sobre el array ya mezclado: así recorta por igual las tandas
    // de esta sesión y el historial, sin que haya que acordarse de aplicarlo dos veces
    // (ADR-2026-09-08-creador-de-video-filtra-por-tipo-de-contenido). El historial ya
    // viene filtrado de la base; lo que este pase esconde son las tandas de imagen de
    // otros modos, incluidos sus recuadros "generando".
    return filtrarPorContenido(out, filtro);
    // `ahora` entra en las deps para que las fechas se refresquen con el reloj.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tandas, creaciones, idsEnTandas, ahora, filtro, eliminadas]);


  /**
   * Las cards que se dibujan AHORA; el resto espera a que el usuario baje —
   * ADR-2026-09-04-las-creaciones-del-panel-se-cargan-por-tanda.
   *
   * El piso son las de la tanda en curso: lo que se está esperando no puede quedar
   * afuera de la primera página por haber pedido muchas imágenes de una.
   */
  const celdasDeLaTanda = React.useMemo(
    () => celdas.filter((c) => c.tipo === 'generando').length,
    [celdas],
  );
  const celdasAMostrar = React.useMemo(
    () => celdas.slice(0, Math.max(celdasVisibles, celdasDeLaTanda)),
    [celdas, celdasVisibles, celdasDeLaTanda],
  );
  // Ids de las cards de IMAGEN visibles, en orden: es lo que la verificación conoce y
  // sobre lo que cuenta las "malas" (ADR-2026-09-07-verificacion-de-calidad-en-la-columna).
  const idsImagenVisibles = React.useMemo(
    () =>
      celdasAMostrar.flatMap((c) => (c.tipo === 'imagen' && !c.esVideo ? [c.id] : [])),
    [celdasAMostrar],
  );
  const tituloPorId = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const c of celdasAMostrar) if (c.tipo === 'imagen') m.set(c.id, c.titulo);
    return m;
  }, [celdasAMostrar]);
  const verificacion = useVerificacion({
    activa: verificacionActiva,
    companyId,
    idsVisibles: idsImagenVisibles,
    tituloDe: (id) => tituloPorId.get(id) ?? '',
    onGeneracionDelegada,
    automatico: analisisAutomatico,
    // Solo lo generado en esta sesión: prender la función no sale a analizar el historial.
    idsDeTandas: idsEnTandas,
  });

  // ── Acciones rápidas de cada card: descargar o enviar a lanzamiento ──────────
  // (ADR-2026-09-07-acciones-rapidas-en-la-card). Los datos de la fila (file_name y
  // productos) no vienen ni con la tanda ni con el listado: se piden por lote de ids
  // visibles, como la verificación. `idsDeCeldas` incluye los VIDEOS, que no se
  // verifican pero sí se descargan.
  const idsDeCeldas = React.useMemo(
    () => celdasAMostrar.flatMap((c) => (c.tipo === 'imagen' ? [c.id] : [])),
    [celdasAMostrar],
  );
  const datosDeCeldas = useDatosDeCeldas(idsDeCeldas, companyId);

  /**
   * Los `full/` de las cards visibles, firmados por adelantado y en UN pedido —
   * ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia.
   *
   * Es la mitad de lo que hacía esperar al visor: su camino largo no puede pedir esta
   * firma hasta tener la fila, porque el path sale del `file_name`. Acá el `file_name` ya
   * está (`useDatosDeCeldas`), así que se firman todas juntas mientras el usuario mira la
   * grilla. Son firmas, no bytes: un request y ninguna descarga.
   *
   * El `Set` es de paths ya pedidos, para que scrollear no vuelva a firmar lo mismo. El
   * resultado no se guarda en estado: `firmarEnLote` deja las URLs en la caché global de
   * firmas, que es de donde las lee el toque.
   */
  const fullPedidas = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    fullPedidas.current = new Set();
  }, [companyId]);
  React.useEffect(() => {
    if (!companyId) return;
    const paths: string[] = [];
    for (const id of idsDeCeldas) {
      const d = datosDeCeldas.get(id);
      if (!d?.fileName) continue;
      const path = `${companyId}/full/${d.fileName}`;
      if (fullPedidas.current.has(path)) continue;
      fullPedidas.current.add(path);
      paths.push(path);
    }
    if (paths.length === 0) return;
    void firmarEnLote(paths).catch(() => {
      // Que se vuelva a intentar cuando cambien los visibles. Sin firma, el toque cae al
      // camino largo del visor: más lento, pero abre igual.
      for (const path of paths) fullPedidas.current.delete(path);
    });
  }, [idsDeCeldas, datosDeCeldas, companyId]);

  /**
   * La fila con la que el visor puede abrir sin salir a la red, o `undefined` si al panel
   * todavía le falta algo (los datos de la card, o la firma del original). La cuenta vive
   * en `filaInicialDelVisor.ts`, compartida con el lienzo del Agente.
   */
  const filaInicialDe = (celda: { id: string; src: string }): FilaInicialVisor | undefined =>
    filaInicialDeCelda({ id: celda.id, src: celda.src, datos: datosDeCeldas.get(celda.id) }, companyId);

  /**
   * El orden que la columna DIBUJA, para que las flechas del visor lo sigan y no salteen
   * la card de al lado (ADR-2026-10-08-las-flechas-del-visor-siguen-a-la-columna). Van
   * todas las traídas y no sólo las dibujadas: las de abajo son las mismas en el mismo
   * orden, y el visor puede seguir bajando aunque la columna no haya scrolleado.
   */
  const ordenDeLaColumna = React.useMemo(
    () => celdas.flatMap((c) => (c.tipo === 'imagen' ? [c.id] : [])),
    [celdas],
  );
  const celdaPorId = React.useMemo(() => {
    const m = new Map<string, { id: string; src: string }>();
    for (const c of celdas) if (c.tipo === 'imagen') m.set(c.id, c);
    return m;
  }, [celdas]);
  /** La fila de una card de este orden, para una vecina que el visor no tiene en su lista. */
  const filaDe = (id: string): FilaInicialVisor | undefined => {
    const celda = celdaPorId.get(id);
    return celda ? filaInicialDe(celda) : undefined;
  };

  const tiendaConectada = useTiendaConectada();
  const { chooserElement, resolverOpciones, aplicarOpciones } = useOpcionesDeDescarga();
  const [descargandoId, setDescargandoId] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  /**
   * Descarga la versión FULL de una creación, con el mismo mecanismo que el resto de la
   * app: la marca de agua se pregunta una vez (si no hay config, resuelve sola) y el
   * blob se entrega por `descargarDesdeBlob`, que en iOS abre la hoja de compartir. El
   * trabajo vive en `descargarCreacion.ts`, compartido con el lienzo del Agente
   * (ADR-2026-09-16-la-descarga-de-una-creacion-sale-a-modulo); acá queda el estado.
   */
  const descargarCreacion = React.useCallback(
    async (datos: DatosCelda, esVideo: boolean) => {
      if (!companyId || !datos.fileName || descargandoId) return;
      setDescargandoId(datos.id);
      setErrorAccion(null);
      try {
        await bajarCreacion({ companyId, datos, esVideo, resolverOpciones, aplicarOpciones });
      } catch (e) {
        console.error('[columna] descarga fallida', e);
        setErrorAccion(e instanceof Error ? e.message : 'No se pudo descargar.');
      } finally {
        setDescargandoId(null);
      }
    },
    [aplicarOpciones, companyId, descargandoId, resolverOpciones],
  );
  /** ¿Queda algo por mostrar? Ya traído, o todavía en la base. */
  const quedaPorMostrar = celdas.length > celdasAMostrar.length || hayMas;

  /**
   * La tanda ya se está dibujando pero el historial todavía viaja —
   * ADR-2026-09-08-la-columna-de-resultados-muestra-el-hueco-del-historial.
   *
   * Mientras dure, debajo de lo que hay van esqueletos. La rama de esqueleto de más
   * abajo no cubre este caso: pide `celdas.length === 0`, y con una tanda en curso SIEMPRE
   * hay celdas, así que el resto de la columna quedaba en blanco hasta que llegaba todo
   * junto. Con los grises el lugar ya está tomado y las cards aterrizan en huecos que
   * estaban, en vez de empujar desde la nada.
   *
   * Se apoya en `creaciones` y no en `celdas`: cuando la tanda termina, sus imágenes son
   * celdas y taparían la pregunta, pero el historial puede seguir sin llegar.
   *
   * `cargando` también se prende al PAGINAR, y ahí no corresponde: para entonces
   * `creaciones` ya tiene la primera página, así que la condición se cae sola.
   */
  const cargandoHistorial = cargando && creaciones.length === 0;

  /**
   * El desfase de entrada que le tocó a cada card la PRIMERA vez que se dibujó, por `key`.
   *
   * No se puede derivar de la posición en cada render: las imágenes de una tanda entran de
   * a una (el backend escribe una fila por vez) y cada una que llega corre un lugar a todas
   * las de abajo. Un `animation-delay` que cambia sobre un elemento ya montado le mueve el
   * reloj a su animación, y una card que había terminado de entrar puede volver a caer
   * dentro de la ventana de 420 ms y parpadear.
   *
   * Escribir el mapa durante el render es seguro acá: la entrada se fija una sola vez por
   * `key` y volver a pasar (el doble render de StrictMode) devuelve lo mismo.
   */
  const desfases = React.useRef(new Map<string, number>());
  const desfaseDe = (key: string, orden: number): number => {
    const guardado = desfases.current.get(key);
    if (guardado !== undefined) return guardado;
    const ms = (orden % CELDAS_POR_PAGINA) * DESFASE_ENTRADA_MS;
    desfases.current.set(key, ms);
    return ms;
  };

  /**
   * Las celdas visibles, cortadas en GRUPOS por fecha —
   * ADR-2026-09-06-la-columna-de-resultados-agrupa-por-fecha.
   *
   * Un solo recorrido y ningún ordenamiento: `celdas` ya viene de lo más nuevo a lo más
   * viejo, así que se abre un grupo cada vez que cambia la clave de `grupoDeFecha`. Se
   * arma sobre las celdas VISIBLES y no sobre todas, así un encabezado aparece recién
   * cuando entra su primera card y la paginación no cambia en nada.
   *
   * Los recuadros "generando" caen naturalmente en "Hoy" —se crearon recién— y quedan
   * arriba de todo, que es donde ya estaban.
   */
  const grupos = React.useMemo(() => {
    const out: {
      clave: string;
      titulo: string;
      celdas: typeof celdasAMostrar;
      /**
       * En qué posición de `celdasAMostrar` arranca el grupo. Cada grupo tiene su propia
       * grilla, así que el índice del `map` es local; el desfase de entrada se cuenta
       * sobre la columna ENTERA para que la ola no vuelva a empezar en cada encabezado.
       */
      desde: number;
    }[] = [];
    for (const [i, celda] of celdasAMostrar.entries()) {
      const { clave, titulo } = grupoDeFecha(celda.creadaEn);
      const ultimo = out[out.length - 1];
      if (ultimo && ultimo.clave === clave) ultimo.celdas.push(celda);
      else out.push({ clave, titulo, celdas: [celda], desde: i });
    }
    return out;
    // `ahora` entra en las deps para que un grupo se renombre solo al cruzar medianoche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celdasAMostrar, ahora]);

  /**
   * El centinela del final: cuando queda a la vista, entra la página siguiente.
   *
   * Se mide con `getBoundingClientRect` sobre un listener de scroll EN CAPTURA, y no con
   * un `IntersectionObserver` — ADR-2026-09-04-el-centinela-se-mide-por-geometria.
   *
   * El observer parecía lo correcto y no funcionaba acá: esta columna vive dentro de un
   * panel que scrollea por su cuenta, y el resultado dependía de qué elemento resultara
   * ser el `root` —el viewport recorta distinto que el contenedor, y con un `root`
   * equivocado el centinela no intersecta NUNCA—. La lista quedaba clavada con los
   * puntitos a la vista, que es el peor de los estados: el que dice "estoy cargando" sin
   * cargar nada.
   *
   * Con geometría no hay `root` que adivinar: se pregunta dónde está el centinela
   * respecto de la ventana. El `true` del listener es lo que hace que el scroll de un
   * contenedor anidado —que NO burbujea— llegue igual.
   *
   * Se chequea también al montar: si el centinela ya está a la vista (una lista corta que
   * no llena la columna), la página siguiente entra sola en vez de esperar un scroll que
   * nadie va a hacer.
   */
  const centinelaRef = React.useRef<HTMLButtonElement | null>(null);
  /**
   * Traer la página siguiente. Primero se dibuja lo que YA está traído; recién cuando se
   * agotó se le pide más a la base — al revés serían filas traídas para guardarlas.
   *
   * `cargando` frena el pedido: mientras una consulta viaja, cada evento de scroll
   * llamaría de nuevo y saldrían tres pedidos encimados por el mismo tramo.
   *
   * Es una función y no el cuerpo del listener porque la dispara TAMBIÉN el click sobre
   * el centinela: si algo del layout deja el chequeo sin disparar, la salida a mano está.
   */
  const cargarMas = React.useCallback(() => {
    if (celdasVisibles < celdas.length) {
      setCeldasVisibles((n) => n + CELDAS_POR_PAGINA);
      return;
    }
    if (hayMas && !cargando) setLimiteCreaciones((l) => l + LIMITE_CREACIONES);
  }, [celdas.length, celdasVisibles, hayMas, cargando]);
  React.useEffect(() => {
    if (!quedaPorMostrar) return;
    const chequear = () => {
      const el = centinelaRef.current;
      if (!el) return;
      // 400px antes de llegar: la página siguiente entra mientras todavía se scrollea,
      // así el recorrido no se frena contra una lista que no creció.
      if (el.getBoundingClientRect().top <= window.innerHeight + 400) cargarMas();
    };
    chequear();
    window.addEventListener('scroll', chequear, true);
    window.addEventListener('resize', chequear);
    return () => {
      window.removeEventListener('scroll', chequear, true);
      window.removeEventListener('resize', chequear);
    };
  }, [quedaPorMostrar, cargarMas]);

  return (
    // `pr-2`: el aire entre las cards y la barra de scroll va ACÁ ADENTRO —el padding de
    // un scroller queda del lado de adentro de su barra—. Nació con la columna "hasta el
    // borde" (ADR-2026-09-04-la-columna-de-resultados-llega-al-borde); esa variante se
    // borró porque movía el panel de la izquierda
    // (ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja), pero los 8px de aire
    // contra la barra siguen valiendo.
    // `gap-6` y no `gap-3`: es la separación ENTRE GRUPOS, y tiene que ser bastante
    // mayor que los 8px que hay entre un encabezado y su grilla — si no, el título queda
    // a la misma distancia del grupo de arriba que del suyo y deja de agrupar nada.
    <div className="flex flex-col gap-6 pb-2 pr-2">
      {/* Sin encabezado de COLUMNA ni títulos por tanda: lo que encabeza es la FECHA
          (ADR-2026-09-06-la-columna-de-resultados-agrupa-por-fecha), y cada card dice de
          qué producto es. Sigue sin haber un bloque por cada toque de Generar, que es lo
          que sacaron ADR-2026-08-25-la-columna-de-resultados-no-lleva-encabezado y
          ADR-2026-09-04-la-columna-de-resultados-va-en-una-grilla-plana. */}
      {cargando && celdas.length === 0 ? (
        <div
          className="grid gap-3"
          style={{
            // TRES columnas fijas y no `auto-fill` (default): con la columna llegando al
            // borde derecho (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde)
            // el auto-fill metía cuatro cards de ~210px, y estas fotos se miran, no se
            // escanean — a tres entran a ~265px en un monitor de 1708
            // (ADR-2026-09-06-la-columna-de-resultados-va-de-a-tres). Ver `columnas`.
            gridTemplateColumns: columnasDeGrilla(columnas),
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <CeldaEsqueleto key={`skel-${i}`} />
          ))}
        </div>
      ) : celdas.length === 0 ? (
        <p className="inline-flex items-center gap-2 text-[12.5px] text-[#3C3C43]/50">
          <ImageOff className="h-4 w-4" />
          {/* Con el filtro puesto el vacío no es el mismo vacío: "no generaste nada" es
              falso si hay cien fotos escondidas detrás del filtro, y deja al usuario sin
              la pista de que alcanza con pasar a "Todo". */}
          {filtro === 'videos'
            ? 'Todavía no generaste ningún video.'
            : 'Todavía no generaste nada.'}
        </p>
      ) : (
        // Un bloque por GRUPO de fecha: encabezado + su propia grilla
        // (ADR-2026-09-06-la-columna-de-resultados-agrupa-por-fecha). Cada grupo lleva
        // su grilla y no una sola compartida: con una sola, un grupo de dos fotos
        // dejaría a las del grupo siguiente terminando esa misma fila.
        grupos.map((grupo, iGrupo) => (
          <div key={grupo.clave} className="flex flex-col gap-2">
            {/* El encabezado, no una card: dice cuándo, y lo que pasa abajo son las
                fotos de ese cuándo. Discreto a propósito —el peso lo tienen las fotos—,
                y el `titulo` vacío (una fecha ilegible) no dibuja nada. */}
            {grupo.titulo && (
              <h3 className="text-[13px] font-semibold leading-tight text-[#3C3C43]/55">
                {grupo.titulo}
              </h3>
            )}
            <div
              className="grid gap-3"
              style={{
                // TRES columnas fijas y no `auto-fill` (default) — mismo motivo que la
                // grilla del esqueleto de arriba. Ver `columnas`.
                gridTemplateColumns: columnasDeGrilla(columnas),
              }}
            >
              {grupo.celdas.map((celda, i) =>
                celda.tipo === 'generando' ? (
                  <RecuadroGenerando
                    key={celda.key}
                    // 4:5 SIEMPRE, como las cards terminadas: con la forma de lo que va a
                    // salir, la grilla se mezclaba justo mientras se genera
                    // (ADR-2026-09-06-la-columna-de-resultados-va-toda-en-4-5).
                    ratio={RATIO_CARD}
                    creadaEn={celda.creadaEn}
                    esVideo={celda.esVideo}
                    etiqueta={etiquetaDeModo(celda.modo)}
                    duracionMs={celda.duracionEstimadaMs}
                  />
                ) : (
                  <div
                    key={celda.key}
                    // `aparece-tras-esqueleto` (fundido + 6px de subida) con un desfase por
                    // posición: la columna se llena como un barrido en vez de aparecer
                    // entera en un frame — ADR-2026-09-08-la-columna-de-resultados-entra-escalonada.
                    //
                    // Es una animación CSS y no una transición de React: corre UNA vez, al
                    // montar el elemento. Este panel se re-renderiza solo (el reloj de las
                    // fechas cada 30 s, la verificación, las acciones rápidas) y con una
                    // transición cada uno de esos renders volvería a barrer la columna.
                    // La clase usa `backwards` a propósito, así que no deja un `transform`
                    // puesto que rompería los `position: fixed` de adentro
                    // (ADR-2026-08-18-la-animacion-de-entrada-no-deja-transform), y trae su
                    // propia guarda de `prefers-reduced-motion` en `index.css` — un
                    // `motion-reduce:` acá no serviría: la regla está después de
                    // `@tailwind utilities` y le gana a la utilidad.
                    data-card-creacion
                    className="group/card relative aparece-tras-esqueleto"
                    style={{
                      // Todas iguales, sin importar el formato con el que se generaron
                      // (ADR-2026-09-06-la-columna-de-resultados-va-toda-en-4-5).
                      aspectRatio: String(RATIO_CARD),
                      animationDelay: `${desfaseDe(celda.key, grupo.desde + i)}ms`,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const fila = filaInicialDe(celda);
                        // La descarga del original arranca ACÁ, antes de que el visor
                        // monte (ver `precargarOriginal`).
                        if (fila) precargarOriginal(fila, companyId, celda.esVideo);
                        setCreacionAbierta(celda.id, fila);
                      }}
                      // Mientras el visor abre, la card tocada se marca con el anillo de
                      // marca (ver `abriendo`). Clases literales en las dos ramas: Tailwind
                      // no compila nombres armados en runtime.
                      // `absolute inset-0` y no `relative`: el contenedor es el `div` de
                      // arriba, para que la capa de verificación sea HERMANA del botón
                      // (un botón dentro de otro es HTML inválido).
                      className={`group absolute inset-0 overflow-hidden rounded-2xl bg-neutral-100 transition-transform active:scale-[0.98] ${
                        abriendo === celda.id
                          ? 'ring-2 ring-brand-primary'
                          : 'ring-1 ring-black/[0.06]'
                      }`}
                    >
                      <MiniaturaCreacion src={celda.src} alt={celda.alt} />
                      {etiquetaDeModo(celda.modo) && (
                        <ChipDeModo texto={etiquetaDeModo(celda.modo)!} />
                      )}
                      {celda.esVideo && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm">
                            <Play className="h-4 w-4 fill-current" />
                          </span>
                        </span>
                      )}
                      {/* El rótulo DENTRO de la foto, abajo, sobre un degradé negro: de qué
                          producto es y cuándo salió. Es la misma anatomía que las cards de
                          Creaciones, que es donde el usuario ya aprendió a leer esos dos
                          datos. El degradé arranca transparente y no llega a un tercio.

                          Con la verificación activa, el botón vive en esta misma esquina
                          inferior derecha (ADR-2026-09-07-verificacion-de-calidad-en-la-columna):
                          el rótulo le hace lugar con `pr-11` para que el nombre se trunque
                          antes en vez de quedar tapado. */}
                      <span
                        className={`pointer-events-none absolute inset-x-0 bottom-0 pl-3 pt-8 pb-2 bg-gradient-to-t ${
                          accionesEnHover ? 'from-black/50 via-black/10' : 'from-black/80 via-black/35'
                        } to-transparent text-left ${
                          verificacionActiva && !celda.esVideo ? 'pr-20' : 'pr-11'
                        } ${accionesEnHover ? 'opacity-0 transition-opacity duration-200 group-hover/card:opacity-100' : ''}`}
                      >
                        {celda.titulo && (
                          <span className="block truncate text-[13px] font-semibold leading-tight text-white">
                            {celda.titulo}
                          </span>
                        )}
                        <span className="mt-0.5 block truncate text-[11.5px] leading-tight text-white/75">
                          {/* Dentro de "Hoy" y "Ayer" el encabezado ya dijo el día, así que
                              la card pone SÓLO la hora; en los grupos gruesos el día sigue
                              haciendo falta y lo trae `fechaSmart`. */}
                          {grupo.clave === 'hoy' || grupo.clave === 'ayer'
                            ? horaDe(celda.creadaEn)
                            : fechaSmart(celda.creadaEn)}
                          {equipo.varios && equipo.nombreDe(celda.userId) && (
                            <> · {equipo.nombreDe(celda.userId)}</>
                          )}
                        </span>
                      </span>
                      {/* El velo de hover. En la card limpia de Redes casi no oscurece: el
                          rótulo y las acciones ya dicen que la card está activa. */}
                      <span
                        className={`pointer-events-none absolute inset-0 bg-black/0 transition-colors ${
                          accionesEnHover ? 'group-hover:bg-black/[0.03]' : 'group-hover:bg-black/10'
                        }`}
                      />
                      {/* Velo de "abriendo…": el toque ya fue registrado y el visor viene. */}
                      {abriendo === celda.id && (
                        <span className="pointer-events-none absolute inset-0 bg-black/20" />
                      )}
                    </button>
                    {/* Acciones rápidas de la card, abajo a la derecha: verificar (si la
                        función está activa y es una imagen) y, a su derecha, descargar o
                        enviar a lanzamiento (ADR-2026-09-07-acciones-rapidas-en-la-card).
                        Con la verificación activa las dibuja `CapaVerificacion`, que es
                        la dueña de esa barra porque su botón comparte fila con la otra
                        acción; sin ella, la acción va sola en su propio contenedor. */}
                    {(() => {
                      const accion = (
                        <AccionRapidaCreacion
                          datos={datosDeCeldas.get(celda.id)}
                          esVideo={celda.esVideo}
                          companyId={companyId}
                          brandId={brandId}
                          tiendaConectada={tiendaConectada}
                          descargando={descargandoId === celda.id}
                          onDescargar={() => {
                            const d = datosDeCeldas.get(celda.id);
                            if (d) void descargarCreacion(d, celda.esVideo);
                          }}
                        />
                      );
                      if (accionesEnHover) {
                        // La columna de acciones de la card limpia, arriba a la derecha.
                        const datos = datosDeCeldas.get(celda.id);
                        const abrirCon = (a: 'carrusel' | 'animar' | 'corregir-voz' | 'estudio') => {
                          if (!datos || !companyId) return;
                          setPedidoHerramienta({
                            celda: { imageId: celda.id, companyId, esVideo: celda.esVideo, src: celda.src, datos, titulo: celda.titulo },
                            herramienta: a,
                            ratio: ratioDeFormato(datos.formato) || 0.8,
                          });
                        };
                        const acciones: { id: string; rotulo: string; Icono: typeof Download; onClick: (e: React.MouseEvent<HTMLElement>) => void; ocupado?: boolean }[] = [
                          {
                            id: 'descargar',
                            rotulo: 'Descargar',
                            Icono: Download,
                            ocupado: descargandoId === celda.id,
                            onClick: () => {
                              if (datos) void descargarCreacion(datos, celda.esVideo);
                            },
                          },
                          ...(accionesEnHover.onRehacer && datos && accionesEnHover.puedeRehacer?.(datos.datosGeneracion)
                            ? [
                                {
                                  id: 'rehacer',
                                  rotulo: 'Rehacer',
                                  Icono: RotateCcw,
                                  onClick: () => accionesEnHover.onRehacer?.(datos.datosGeneracion),
                                },
                              ]
                            : []),
                          ...(celda.esVideo
                            ? [
                                // Lo que la barra del lienzo ofrece para un video, acá también:
                                // corregir una palabra (gratis) y abrirlo en el Estudio.
                                { id: 'corregir-voz', rotulo: 'Corregir voz', Icono: AudioLines, onClick: () => abrirCon('corregir-voz') },
                                { id: 'estudio', rotulo: 'Estudio de video', Icono: Clapperboard, onClick: () => abrirCon('estudio') },
                              ]
                            : [
                                { id: 'carrusel', rotulo: 'Carrusel', Icono: Images, onClick: () => abrirCon('carrusel') },
                                { id: 'animar', rotulo: 'Animar imagen', Icono: Clapperboard, onClick: () => abrirCon('animar') },
                              ]),
                        ];
                        // "Añadir": la card entra a la caja (imagen como referencia, video
                        // como video de referencia; la pantalla decide qué hace y pasa a Video).
                        // Va aparte, como pastilla con texto abajo a la derecha, para imagen y
                        // video por igual (ADR-2026-10-07-anadir-es-una-pastilla-en-la-card;
                        // antes "Editar" / "Añadir como referencia" en la columna de íconos).
                        const anadir = (e: React.MouseEvent<HTMLElement>) => {
                          if (!datos || !companyId) return;
                          const adjunto = adjuntoDeCelda({ imageId: celda.id, companyId, esVideo: celda.esVideo, src: celda.src, datos, titulo: celda.titulo });
                          const card = (e.currentTarget as HTMLElement).closest('[data-card-creacion]');
                          if (adjunto) accionesEnHover.onEditar(adjunto, card ? { rect: card.getBoundingClientRect(), src: celda.src } : undefined);
                        };
                        return (
                          <>
                          <div className="absolute bottom-2 right-2 z-20 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover/card:opacity-100">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                anadir(e);
                              }}
                              disabled={!datos}
                              aria-label={celda.esVideo ? 'Añadir el video a la caja' : 'Añadir la imagen a la caja'}
                              className="inline-flex h-8 items-center gap-1.5 rounded-full bg-black/55 py-1.5 pl-2.5 pr-3.5 text-[12.5px] font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-black/75 disabled:opacity-60"
                            >
                              <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                              Añadir
                            </button>
                          </div>
                          <div className="absolute right-2 top-2 z-20 flex flex-col items-end gap-1.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover/card:opacity-100">
                            {acciones.map(({ id, rotulo, Icono, onClick, ocupado }) => (
                              <div key={id} className="group/accion relative flex items-center">
                                {/* El nombre, a la IZQUIERDA del botón, al pasar por él. */}
                                <span
                                  aria-hidden="true"
                                  className="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-full bg-black/70 px-2.5 py-1 text-[12px] font-medium text-white opacity-0 backdrop-blur-sm transition-opacity group-hover/accion:opacity-100 group-focus-visible/accion:opacity-100"
                                >
                                  {rotulo}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onClick(e);
                                  }}
                                  disabled={ocupado}
                                  aria-label={rotulo}
                                  title={rotulo}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-black/70 disabled:opacity-60"
                                >
                                  {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icono className="h-4 w-4" strokeWidth={2} />}
                                </button>
                              </div>
                            ))}
                          </div>
                          </>
                        );
                      }
                      return verificacionActiva && !celda.esVideo ? (
                        <CapaVerificacion
                          id={celda.id}
                          api={verificacion}
                          accionExtra={accion}
                          onEditorAbierto={setEditorProductoAbierto}
                        />
                      ) : (
                        <div className="absolute bottom-2 right-2 z-20 flex items-center gap-1.5">
                          {accion}
                        </div>
                      );
                    })()}
                  </div>
                ),
              )}
              {/* El hueco de lo que todavía viaja, en la MISMA grilla y no en una aparte:
                  así los grises tapan primero los lugares vacíos que dejó la última fila
                  de la tanda, y las cards del historial entran justo ahí
                  (ADR-2026-09-08-la-columna-de-resultados-muestra-el-hueco-del-historial).
                  Van en el último grupo porque es el final de la columna, que es por donde
                  se sigue leyendo. */}
              {cargandoHistorial &&
                iGrupo === grupos.length - 1 &&
                Array.from({
                  length: esqueletosDeRelleno(grupo.celdas.length, columnas),
                }).map((_, i) => <CeldaEsqueleto key={`hueco-${i}`} />)}
            </div>
          </div>
        ))
      )}

      {/* El final de la lista: cuando aparece, entra la próxima página de cards
          (ADR-2026-09-04-las-creaciones-del-panel-se-cargan-por-tanda). Los tres puntos
          no son decorativos: un contenedor de alto 0 puede quedar intersectando para
          siempre y la lista crecería sola de una. */}
      {quedaPorMostrar && (
        // Y además se puede TOCAR: es la salida cuando el observer no llega a dispararse.
        <button
          type="button"
          ref={centinelaRef}
          onClick={cargarMas}
          aria-label="Ver más creaciones"
          className="flex items-center justify-center py-3 text-[12.5px] text-[#3C3C43]/45 transition-colors hover:text-[#3C3C43]/70"
        >
          <span className="inline-flex gap-1">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
          </span>
        </button>
      )}

      {/* Selector de marca de agua de la descarga: se monta UNA vez para toda la columna
          y solo aparece si la marca tiene la función configurada
          (ADR-2026-09-07-acciones-rapidas-en-la-card). */}
      {chooserElement}

      {/* La columna no tiene sistema de toast: el error de una acción rápida se muestra
          portaleado al body, arriba del sheet, y se va al tocarlo. Nunca un catch mudo. */}
      {errorAccion &&
        createPortal(
          <button
            type="button"
            onClick={() => setErrorAccion(null)}
            className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4"
          >
            <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-5 text-sm text-white shadow-xl">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
              {errorAccion}
            </span>
          </button>,
          document.body,
        )}

      {accionesEnHover && (
        <PanelDeHerramienta
          pedido={pedidoHerramienta}
          onCerrar={() => setPedidoHerramienta(null)}
          onGeneracion={(g) => {
            setPedidoHerramienta(null);
            onGeneracionDelegada?.(g);
          }}
          onImagenNueva={() => setPedidoHerramienta(null)}
          onError={(m) => setErrorAccion(m)}
        />
      )}

      {/* El componente se monta acá pero NO se dibuja acá: su lightbox se
          portaliza solo al `body`. Lo único que hace falta de este lado es que
          el sheet baje su z mientras está abierto (`onVisorChange`). */}
      {creacionAbierta && (
        <GeneratedImagesHistory
          soloVisor={{
            imageId: creacionAbierta,
            // Con esto el visor no sale a buscar la fila ni la firma: abre con lo que el
            // panel ya tenía (ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia).
            filaInicial: filaInicialVisor,
            // Las flechas recorren la columna tal como se ve, no el `created_at` de la
            // lista del visor (ADR-2026-10-08-las-flechas-del-visor-siguen-a-la-columna).
            orden: ordenDeLaColumna,
            filaDe,
            ...(accionInicialVisor ? { accionInicial: accionInicialVisor } : {}),
            // La lista de atrás del visor arranca filtrada por la marca activa: si
            // arrancara sin filtro y después "siguiera a la marca", un segundo fetch
            // pisaría la lista con el visor ya abierto
            // (ADR-2026-09-06-el-visor-prestado-abre-por-id).
            brandId,
            onCerrar: () => setCreacionAbierta(null),
            // El visor ya nació: la card tocada deja de decir "abriendo".
            onAbierto: () => setAbriendo(null),
            // La creación no existe o no se pudo cargar: se desmonta el visor, y con
            // `creacionAbierta` en null el sheet recupera su z-index (`onVisorChange`).
            onError: () => setCreacionAbierta(null),
            onGeneracionDelegada,
          }}
        />
      )}
    </div>
  );
};

export default PanelTandaCreaciones;
